import { checkValidation, ValidationType } from '@src/helpers/checkValidation'
import { mixpanelService } from '@src/srv/mixpanel.service'
import classNames from 'classnames'
import type { ReactNode } from 'react'
import {
  ChangeEvent,
  Dispatch,
  InputHTMLAttributes,
  KeyboardEvent,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react'
import styles from './inputNc.module.scss'

interface InputNcProps {
  id: string
  setValue: Dispatch<SetStateAction<string>>
  type: 'email' | 'text' | 'number' | 'tel'
  name?: string
  label: string
  theme?: 'dark' | 'light'
  autoComplete?: 'on' | 'off'
  showErrorFromParent?: boolean
  errorMessage?: string
  placeHolder?: string
  mixPanelTrackProp?: string
  mixPanelServiceTrackName?: string
  keyPressEvent?: (e: KeyboardEvent<HTMLInputElement>) => void
}

export function InputNc({
  setValue,
  type,
  id,
  label,
  errorMessage,
  placeHolder,
  mixPanelTrackProp,
  mixPanelServiceTrackName,
  autoComplete = 'off',
  name,
  showErrorFromParent = false,
  keyPressEvent,
  theme,
}: InputNcProps): ReactNode {
  const [validInput, setValidInput] = useState(false)
  const [focus, setFocus] = useState(false)
  const [showError, setShowError] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const error = errorMessage || 'Please enter valid value!'

  useEffect(() => {
    setShowError(showErrorFromParent)
    if (validInput) {
      setValidInput(!showErrorFromParent)
    }
  }, [showErrorFromParent])

  function checkForValidEmail(e: ChangeEvent<HTMLInputElement>): void {
    const input = e.target.value

    const validEmail = checkValidation(input, ValidationType.Email)
    if (validEmail) {
      setValidInput(true)
      setShowError(false)
      setFocus(true)
      setValue(input)
    } else {
      setValidInput(false)
      setShowError(true)
      if (!input) {
        setFocus(false)
      }
    }
  }

  function checkForValidTel(e: ChangeEvent<HTMLInputElement>): void {
    const input = e.target.value
    const validTel = checkValidation(input, ValidationType.Tel)

    if (validTel) {
      setShowError(false)
      setValidInput(true)
      setFocus(true)
      setValue(input)
    } else {
      setShowError(true)
      setValidInput(false)
    }
  }

  function checkForValidText(e: ChangeEvent<HTMLInputElement>): void {
    const input = e.target.value
    if (!input) {
      setFocus(false)
      setValidInput(false)
      setShowError(true)
    } else {
      setValidInput(true)
      setShowError(false)
    }
  }

  function handleFocus(): void {
    setShowError(false)
    setFocus(true)

    if (mixPanelServiceTrackName && mixPanelTrackProp) {
      mixpanelService.track(mixPanelTrackProp, {
        mixPanelServiceTrackName,
      })
    }
  }

  function handleChange(e: ChangeEvent<HTMLInputElement>): void {
    const input = e.target.value
    setValue(input)
  }

  function handleBlur(e: ChangeEvent<HTMLInputElement>): void {
    if (type === 'email') {
      checkForValidEmail(e)
    } else if (type === 'text') {
      checkForValidText(e)
    } else if (type === 'tel') {
      checkForValidTel(e)
    }
  }

  function handleKeyPress(e: KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Enter') {
      if (inputRef.current) {
        const syntheticChangeEvent = {
          target: inputRef.current,
        } as ChangeEvent<HTMLInputElement>

        if (type === 'email') {
          checkForValidEmail(syntheticChangeEvent)
        }
        if (type === 'tel') {
          checkForValidTel(syntheticChangeEvent)
        }
      }

      if (keyPressEvent) {
        keyPressEvent(e)
      }
    }
  }

  const classInput = classNames(styles.Input, {
    [styles.Input__focus]: focus,
    [styles.Input__alert]: showError,
    [styles.Input___light]: theme === 'light',
  })

  const classLabel = classNames(styles.Label, { [styles.Label__focus]: focus })

  const inputProps: InputHTMLAttributes<HTMLInputElement> = {
    name,
    type,
    id,
    autoComplete,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onChange: handleChange,
    onKeyPress: e => handleKeyPress(e),
    placeholder: placeHolder || undefined,
    className: classInput,
  }

  return (
    <div className={styles.Input__position}>
      <label className={classLabel} htmlFor={id}>
        {label}
      </label>
      <input {...inputProps} ref={inputRef} />
      {validInput && (
        /* eslint-disable-next-line jsx-a11y/alt-text */
        <img src="/icons/input-check.svg" aria-hidden={true} className={styles.Input__checkIcon} />
      )}
      {showError && (
        <div className={styles.ErrorMessage} aria-live="assertive" role="alert">
          <img src="/icons/input-warning.svg" aria-hidden={true} alt="input-error" />
          <span>{error}</span>
        </div>
      )}
    </div>
  )
}
